import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Popover,
  Button,
  Checkbox,
  Alert,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Candidature,
  createCandidature,
  listCandidatures,
  updateCandidature,
  updateCandidatureDocumentsOnly,
  updateCandidatureStatus,
} from '../api/candidatures';
import {
  deleteCandidatureStorageFileByUrl,
  uploadCandidaturePdf,
} from '../api/candidatureStorage';
import { deleteDocsInCollection } from '../api/batchDelete';
import { listUsers } from '../api/users';
import { listPostsSummary } from '../api/posts';
import { listSchoolsSummary } from '../api/schools';
import { useAuth } from '../auth/AuthContext';
import { useScopedSchoolIds } from '../auth/useScope';
import { candidatureStatusLabel } from '../utils/statusLabels';
import { normalizeText } from '../utils/text';
import { downloadRecordsCsv } from '../utils/csvExport';
import {
  EntityAutocomplete,
  type EntityOption,
} from '../components/EntityAutocomplete';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'pending':
    default:
      return 'warning';
  }
};

function dateToInputValue(d?: Date): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function inputValueToDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
}

function normalizeDocumentUrls(rows: string[]): string[] {
  return rows.map((s) => s.trim()).filter(Boolean);
}

function fileNameFromUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    const pathname = new URL(trimmed).pathname;
    const seg = pathname.split('/').filter(Boolean);
    const last = seg[seg.length - 1];
    return last ? decodeURIComponent(last) : trimmed;
  } catch {
    return trimmed.length > 52 ? `${trimmed.slice(0, 49)}…` : trimmed;
  }
}

type DocumentsListEditorProps = {
  documents: string[];
  onAddDocumentUrl: (url: string) => void;
  onRemoveDocument: (index: number) => Promise<void>;
  disabled?: boolean;
  uploadPdf?: (file: File) => Promise<string>;
};

/** Liens en lecture seule ; ajout par upload PDF ; suppression via parent (Storage + Firestore si besoin). */
function DocumentsListEditor({
  documents,
  onAddDocumentUrl,
  onRemoveDocument,
  disabled,
  uploadPdf,
}: DocumentsListEditorProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = React.useState<number | null>(null);
  const [confirmDeleting, setConfirmDeleting] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadPdf) return;
    setLocalError(null);
    setUploading(true);
    try {
      const url = await uploadPdf(file);
      onAddDocumentUrl(url);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Envoi du fichier impossible.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Documents
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Les liens ne sont pas modifiables. Ajoutez des PDF via le bouton ci-dessous. La suppression retire
        le fichier Firebase Storage lorsque l’URL provient de ce projet.
      </Typography>
      {localError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}
      {documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Aucun document pour l’instant.
        </Typography>
      ) : null}
      {documents.map((url, index) => {
        const trimmed = url.trim();
        const canOpen = /^https?:\/\//i.test(trimmed);
        const label = fileNameFromUrl(trimmed) || `Document ${index + 1}`;
        const dialogOpen = pendingDeleteIndex !== null;
        return (
          <Box
            key={`${index}-${trimmed.slice(0, 24)}`}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={trimmed}
            >
              {label}
            </Typography>
            <Tooltip title="Ouvrir dans un nouvel onglet">
              <span>
                <IconButton
                  size="small"
                  component="a"
                  href={canOpen ? trimmed : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={disabled || dialogOpen || !canOpen}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Supprimer ce document">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    setLocalError(null);
                    setPendingDeleteIndex(index);
                  }}
                  disabled={disabled || dialogOpen || uploading || confirmDeleting}
                  color="error"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={handleFileChange}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || uploading || !uploadPdf}
        sx={{ mt: 0.5 }}
      >
        {uploading ? 'Envoi…' : 'Ajouter un document (PDF)'}
      </Button>
      {!uploadPdf && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Sélectionnez d’abord l’utilisateur pour pouvoir envoyer des fichiers.
        </Typography>
      )}

      <Dialog
        open={pendingDeleteIndex !== null}
        onClose={() => {
          if (!confirmDeleting) setPendingDeleteIndex(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Supprimer ce document ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action est définitive : le fichier sera supprimé du stockage Firebase lorsque
            l’URL provient de ce projet, et retiré de la candidature.
          </DialogContentText>
          {pendingDeleteIndex !== null && documents[pendingDeleteIndex] != null ? (
            <Typography
              variant="body2"
              sx={{ mt: 2, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={documents[pendingDeleteIndex]}
            >
              {fileNameFromUrl(documents[pendingDeleteIndex].trim()) ||
                `Document ${pendingDeleteIndex + 1}`}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeleteIndex(null)} color="inherit" disabled={confirmDeleting}>
            Annuler
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={confirmDeleting || pendingDeleteIndex === null}
            onClick={() => {
              if (pendingDeleteIndex === null) return;
              const idx = pendingDeleteIndex;
              setLocalError(null);
              setConfirmDeleting(true);
              void (async () => {
                try {
                  await onRemoveDocument(idx);
                  setPendingDeleteIndex(null);
                } catch (err) {
                  setLocalError(
                    err instanceof Error ? err.message : 'Suppression impossible.'
                  );
                } finally {
                  setConfirmDeleting(false);
                }
              })();
            }}
          >
            {confirmDeleting ? 'Suppression…' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export const CandidaturesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canEdit = role === 'admin';
  const { scopedSchoolIds } = useScopedSchoolIds();
  const { data, isLoading } = useQuery<Candidature[]>({
    queryKey: ['candidatures'],
    queryFn: listCandidatures,
  });
  const { data: users } = useQuery({
    queryKey: ['users-for-candidatures'],
    queryFn: listUsers,
  });
  const { data: posts } = useQuery({
    queryKey: ['posts-summary'],
    queryFn: listPostsSummary,
  });
  const { data: schoolsForPostLabels } = useQuery({
    queryKey: ['schools-for-candidature-post-labels'],
    queryFn: listSchoolsSummary,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'user' | 'post' | 'description' | 'documents' | 'date' | 'createdAt' | 'updatedAt' | 'status'
  >('date');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const [confirmAnchorEl, setConfirmAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [confirmDescription, setConfirmDescription] = React.useState('');
  const [pendingAction, setPendingAction] = React.useState<{
    ids: string[];
    status: 'approved' | 'rejected';
  } | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [editCandidature, setEditCandidature] = React.useState<Candidature | null>(null);
  const [editUserId, setEditUserId] = React.useState('');
  const [editPostId, setEditPostId] = React.useState('');
  const [editCandStatus, setEditCandStatus] = React.useState<'approved' | 'rejected' | 'pending'>(
    'pending'
  );
  const [editCandDate, setEditCandDate] = React.useState('');
  const [editDescription, setEditDescription] = React.useState('');
  const [editDocuments, setEditDocuments] = React.useState<string[]>([]);
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createUserId, setCreateUserId] = React.useState('');
  const [createPostId, setCreatePostId] = React.useState('');
  const [createCandStatus, setCreateCandStatus] = React.useState<
    'approved' | 'rejected' | 'pending'
  >('pending');
  const [createCandDate, setCreateCandDate] = React.useState('');
  const [createDescription, setCreateDescription] = React.useState('');
  const [createDocuments, setCreateDocuments] = React.useState<string[]>([]);
  const [createSaving, setCreateSaving] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  /** Dossier Storage temporaire pour les PDF avant création du doc Firestore (même logique que l’app mobile). */
  const createFolderKeyRef = React.useRef<string>('');

  const confirmOpen = Boolean(confirmAnchorEl);

  const handleRequestSort = (
    property:
      | 'user'
      | 'post'
      | 'description'
      | 'documents'
      | 'date'
      | 'createdAt'
      | 'updatedAt'
      | 'status'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const userNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    users?.forEach((u) => {
      const fullName = `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim();
      map[u.id] = fullName || u.email || u.id;
    });
    return map;
  }, [users]);

  const postTitleById = React.useMemo(() => {
    const map: Record<string, string> = {};
    posts?.forEach((p) => {
      map[p.id] = p.title || p.id;
    });
    return map;
  }, [posts]);

  const postSchoolById = React.useMemo(() => {
    const map: Record<string, string> = {};
    posts?.forEach((p) => {
      if (p.schoolId) map[p.id] = p.schoolId;
    });
    return map;
  }, [posts]);

  const schoolNameByIdForPosts = React.useMemo(() => {
    const map: Record<string, string> = {};
    schoolsForPostLabels?.forEach((s) => {
      map[s.id] = (s.name ?? '').trim() || '';
    });
    return map;
  }, [schoolsForPostLabels]);

  const editDialogUserOptions = React.useMemo<EntityOption[]>(() => {
    if (!users?.length) return [];
    return [...users]
      .sort((a, b) => {
        const la = `${a.lastName ?? ''} ${a.firstName ?? ''}`.trim() || a.email || a.id;
        const lb = `${b.lastName ?? ''} ${b.firstName ?? ''}`.trim() || b.email || b.id;
        return la.localeCompare(lb, 'fr', { sensitivity: 'base' });
      })
      .map((u) => {
        const label =
          `${`${u.lastName ?? ''} ${u.firstName ?? ''}`.trim() || u.email || 'Profil'}`;
        const description =
          [u.academy, u.commune, u.speciality]
            .map((x) => (typeof x === 'string' ? x.trim() : ''))
            .filter(Boolean)
            .join(' · ') || 'Académie, commune et spécialité non renseignées';
        return { id: u.id, label, description };
      });
  }, [users]);

  const editDialogPostOptions = React.useMemo<EntityOption[]>(() => {
    if (!posts?.length) return [];
    return [...posts]
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '', 'fr', { sensitivity: 'base' }))
      .map((p) => {
        const schoolName =
          p.schoolId && schoolNameByIdForPosts[p.schoolId]
            ? schoolNameByIdForPosts[p.schoolId]
            : null;
        const title = p.title?.trim() || 'Sans titre';
        const label = schoolName ? `${title} · ${schoolName}` : title;
        return { id: p.id, label };
      });
  }, [posts, schoolNameByIdForPosts]);

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((c) => {
      // cap/ae : uniquement les candidatures rattachées à leurs écoles (via le poste).
      if (scopedSchoolIds) {
        const schoolId = c.postId ? postSchoolById[c.postId] : undefined;
        if (!schoolId || !scopedSchoolIds.has(schoolId)) return false;
      }
      const haystack = normalizeText(`${c.id} ${userNameById[c.userId ?? ''] ?? c.userId ?? ''} ${postTitleById[c.postId ?? ''] ?? c.postId ?? ''} ${c.description ?? ''} ${(c.documents ?? []).join(' ')}`);
      const matchesSearch = haystack.includes(normalizeText(search));
      const matchesStatus =
        statusFilter === 'all' || (c.status ?? 'pending') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter, userNameById, postTitleById, postSchoolById, scopedSchoolIds]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      let av: string | Date | number | undefined;
      let bv: string | Date | number | undefined;

      switch (orderBy) {
        case 'user':
          av = userNameById[a.userId ?? ''] ?? (a.userId ?? '').toLowerCase();
          bv = userNameById[b.userId ?? ''] ?? (b.userId ?? '').toLowerCase();
          break;
        case 'post':
          av = postTitleById[a.postId ?? ''] ?? (a.postId ?? '').toLowerCase();
          bv = postTitleById[b.postId ?? ''] ?? (b.postId ?? '').toLowerCase();
          break;
        case 'description':
          av = (a.description ?? '').toLowerCase();
          bv = (b.description ?? '').toLowerCase();
          break;
        case 'documents':
          av = a.documents?.length ?? 0;
          bv = b.documents?.length ?? 0;
          break;
        case 'date':
          av = a.date ?? new Date(0);
          bv = b.date ?? new Date(0);
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        case 'status':
          av = (a.status ?? '').toString().toLowerCase();
          bv = (b.status ?? '').toString().toLowerCase();
          break;
        default:
          av = 0 as any;
          bv = 0 as any;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy, userNameById, postTitleById]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleChangeStatusRequest = (
    ids: string[],
    status: 'approved' | 'rejected'
  ) => {
    if (!data || ids.length === 0) return;
    const actionLabel = status === 'approved' ? 'approuver' : 'rejeter';

    if (ids.length === 1) {
      const id = ids[0];
      const candidature = data.find((c) => c.id === id);
      const candidateName = candidature?.userId
        ? userNameById[candidature.userId] ?? candidature.userId
        : 'ce candidat';
      const postTitle = candidature?.postId
        ? postTitleById[candidature.postId] ?? candidature.postId
        : 'ce poste';

      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} la candidature de ${candidateName} pour ${postTitle} ?`
      );
    } else {
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} les ${ids.length} candidatures sélectionnées ?`
      );
    }

    setPendingAction({ ids, status });
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    await Promise.all(
      pendingAction.ids.map((id) =>
        updateCandidatureStatus(id, pendingAction.status)
      )
    );
    await queryClient.invalidateQueries({ queryKey: ['candidatures'] });
    setConfirmAnchorEl(null);
    setPendingAction(null);
    setSelectedIds([]);
  };

  const handleCancelAction = () => {
    setConfirmAnchorEl(null);
    setPendingAction(null);
  };

  const openEditCandidature = (c: Candidature) => {
    setEditCandidature(c);
    setEditUserId(c.userId ?? '');
    setEditPostId(c.postId ?? '');
    setEditCandStatus((c.status as 'approved' | 'rejected' | 'pending') ?? 'pending');
    setEditCandDate(dateToInputValue(c.date));
    setEditDescription(c.description ?? '');
    setEditDocuments(c.documents?.length ? [...c.documents] : []);
    setEditError(null);
  };

  const closeEditCandidature = () => {
    if (editSaving) return;
    setEditCandidature(null);
    setEditError(null);
  };

  const openCreateCandidature = () => {
    setEditCandidature(null);
    setEditError(null);
    setCreateUserId('');
    setCreatePostId('');
    setCreateCandStatus('pending');
    setCreateCandDate(dateToInputValue(new Date()));
    setCreateDescription('');
    setCreateDocuments([]);
    setCreateError(null);
    createFolderKeyRef.current =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `draft-${Date.now()}`;
    setCreateOpen(true);
  };

  const closeCreateCandidature = () => {
    if (createSaving) return;
    setCreateOpen(false);
    setCreateError(null);
  };

  const handleRemoveCreateDocument = async (index: number) => {
    const url = createDocuments[index];
    if (url == null) throw new Error('Document introuvable.');
    const next = createDocuments.filter((_, i) => i !== index);
    await deleteCandidatureStorageFileByUrl(url);
    setCreateDocuments(next);
  };

  const handleRemoveEditDocument = async (index: number) => {
    if (!editCandidature) throw new Error('Candidature introuvable.');
    const url = editDocuments[index];
    if (url == null || !url.trim()) throw new Error('Document introuvable.');
    const next = editDocuments.filter((_, i) => i !== index);
    await deleteCandidatureStorageFileByUrl(url);
    await updateCandidatureDocumentsOnly(editCandidature.id, normalizeDocumentUrls(next));
    setEditDocuments(next);
    await queryClient.invalidateQueries({ queryKey: ['candidatures'] });
  };

  const handleSaveCreateCandidature = async () => {
    if (!createUserId.trim() || !createPostId.trim()) {
      setCreateError('Utilisateur et poste sont obligatoires.');
      return;
    }
    const d = inputValueToDate(createCandDate);
    if (!d) {
      setCreateError('Date invalide (AAAA-MM-JJ).');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      await createCandidature({
        userId: createUserId.trim(),
        postId: createPostId.trim(),
        status: createCandStatus,
        date: d,
        description: createDescription,
        documents: normalizeDocumentUrls(createDocuments),
      });
      await queryClient.invalidateQueries({ queryKey: ['candidatures'] });
      setCreateOpen(false);
      setPage(0);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSaveCandidature = async () => {
    if (!editCandidature) return;
    if (!editUserId.trim() || !editPostId.trim()) {
      setEditError('userId et postId sont obligatoires.');
      return;
    }
    const d = inputValueToDate(editCandDate);
    if (!d) {
      setEditError('Date invalide (AAAA-MM-JJ).');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updateCandidature(editCandidature.id, {
        userId: editUserId.trim(),
        postId: editPostId.trim(),
        status: editCandStatus,
        date: d,
        description: editDescription,
        documents: normalizeDocumentUrls(editDocuments),
      });
      await queryClient.invalidateQueries({ queryKey: ['candidatures'] });
      setEditCandidature(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleConfirmDeleteSelected = async () => {
    if (!selectedIds.length) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDocsInCollection('candidatures', selectedIds);
      await queryClient.invalidateQueries({ queryKey: ['candidatures'] });
      setSelectedIds([]);
      setDeleteDialogOpen(false);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "La suppression a échoué.");
    } finally {
      setDeleting(false);
    }
  };

  const allSelected =
    filtered.length > 0 &&
    filtered.every((c) => selectedIds.includes(c.id));
  const someSelected =
    filtered.length > 0 &&
    filtered.some((c) => selectedIds.includes(c.id)) &&
    !allSelected;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filtered.map((c) => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleExportCsv = () => {
    const rows: Array<Record<string, unknown>> = sorted.map((c) => {
      const uid = c.userId ?? '';
      const pid = c.postId ?? '';
      return {
        candidatureId: c.id,
        userId: uid,
        userName: uid ? userNameById[uid] ?? '' : '',
        postId: pid,
        postTitle: pid ? postTitleById[pid] ?? '' : '',
        status: c.status ?? '',
        date: c.date,
        description: c.description ?? '',
        documents: (c.documents ?? []).join('|'),
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
      };
    });
    downloadRecordsCsv('candidatures', rows);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Candidatures
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="ID, candidat, poste, description, URL document…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="approved">Approuvée</MenuItem>
            <MenuItem value="rejected">Rejetée</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 'auto' }, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openCreateCandidature}
            >
              Ajouter
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            disabled={!sorted.length}
            onClick={handleExportCsv}
          >
            Exporter CSV
          </Button>
        </Box>
      </Box>
      {canEdit && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedIds.length
              ? `${selectedIds.length} sélectionnée(s)`
              : 'Aucune sélection'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={!selectedIds.length}
              onClick={(event) => {
                setConfirmAnchorEl(event.currentTarget);
                handleChangeStatusRequest(selectedIds, 'approved');
              }}
            >
              Approuver la sélection
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={!selectedIds.length}
              onClick={(event) => {
                setConfirmAnchorEl(event.currentTarget);
                handleChangeStatusRequest(selectedIds, 'rejected');
              }}
            >
              Rejeter la sélection
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={!selectedIds.length}
              onClick={() => {
                setDeleteError(null);
                setDeleteDialogOpen(true);
              }}
            >
              Supprimer la sélection
            </Button>
          </Box>
        </Box>
      )}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canEdit && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
              )}
              <TableCell sortDirection={orderBy === 'user' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'user'}
                  direction={orderBy === 'user' ? order : 'asc'}
                  onClick={() => handleRequestSort('user')}
                >
                  Candidat
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'post' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'post'}
                  direction={orderBy === 'post' ? order : 'asc'}
                  onClick={() => handleRequestSort('post')}
                >
                  Poste
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'description' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'description'}
                  direction={orderBy === 'description' ? order : 'asc'}
                  onClick={() => handleRequestSort('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'documents' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'documents'}
                  direction={orderBy === 'documents' ? order : 'asc'}
                  onClick={() => handleRequestSort('documents')}
                >
                  Documents
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'date' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={() => handleRequestSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créé le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mis à jour le
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              {canEdit && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={canEdit ? 11 : 10}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((c) => (
                <TableRow key={c.id} hover selected={canEdit && selectedIds.includes(c.id)}>
                  {canEdit && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(c.id)}
                        onChange={() => toggleOne(c.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {c.userId ? userNameById[c.userId] ?? c.userId : '—'}
                  </TableCell>
                  <TableCell>
                    {c.postId ? postTitleById[c.postId] ?? c.postId : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220, verticalAlign: 'top' }}>
                    {(() => {
                      const raw = (c.description ?? '').trim();
                      const display = raw || '—';
                      return (
                        <Tooltip title={raw} disableHoverListener={!raw} placement="top-start">
                          <Typography
                            variant="body2"
                            color={raw ? 'text.primary' : 'text.secondary'}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {display}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {c.documents?.length ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
                        <Tooltip
                          title={c.documents.join('\n')}
                          placement="top-start"
                        >
                          <Typography variant="body2">
                            {c.documents.length} fichier{c.documents.length > 1 ? 's' : ''}
                          </Typography>
                        </Tooltip>
                        {c.documents.slice(0, 2).map((url, i) => (
                          <Link
                            key={`${url}-${i}`}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            PDF
                          </Link>
                        ))}
                        {c.documents.length > 2 && (
                          <Typography variant="caption" color="text.secondary">
                            +{c.documents.length - 2} autre{c.documents.length - 2 > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(c.date)}</TableCell>
                  <TableCell>{formatDate(c.createdAt)}</TableCell>
                  <TableCell>{formatDate(c.updatedAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={candidatureStatusLabel(c.status)}
                      color={statusColor(c.status) as any}
                      size="small"
                    />
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          flexWrap: 'nowrap',
                          gap: 0.25,
                        }}
                      >
                        <Tooltip title="Modifier la candidature">
                          <IconButton size="small" onClick={() => openEditCandidature(c)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {c.status !== 'approved' && (
                          <Tooltip title="Approuver la candidature">
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                setConfirmAnchorEl(event.currentTarget);
                                handleChangeStatusRequest([c.id], 'approved');
                              }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {c.status !== 'rejected' && (
                          <Tooltip title="Rejeter la candidature">
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                setConfirmAnchorEl(event.currentTarget);
                                handleChangeStatusRequest([c.id], 'rejected');
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={canEdit ? 11 : 10}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune candidature trouvée.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100, 500]}
      />
      <Dialog open={createOpen} onClose={closeCreateCandidature} maxWidth="md" fullWidth>
        <DialogTitle>Nouvelle candidature</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {createError && (
            <Alert severity="error" onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          <EntityAutocomplete
            label="Utilisateur"
            required
            options={editDialogUserOptions}
            valueId={createUserId}
            onChangeId={setCreateUserId}
            disabled={createSaving}
            loading={!users}
            helperText="Rechercher par nom ou e-mail"
            orphanLabel={createUserId.trim() ? 'Utilisateur (hors liste chargée)' : undefined}
          />
          <EntityAutocomplete
            label="Poste"
            required
            options={editDialogPostOptions}
            valueId={createPostId}
            onChangeId={setCreatePostId}
            disabled={createSaving}
            loading={!posts}
            helperText="Rechercher par titre ou nom d’école"
            orphanLabel={createPostId.trim() ? 'Poste (hors liste chargée)' : undefined}
          />
          <TextField
            label="Date"
            type="date"
            value={createCandDate}
            onChange={(e) => setCreateCandDate(e.target.value)}
            disabled={createSaving}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Description"
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            disabled={createSaving}
            multiline
            minRows={3}
          />
          <DocumentsListEditor
            documents={createDocuments}
            onAddDocumentUrl={(url) => setCreateDocuments((d) => [...d, url])}
            onRemoveDocument={handleRemoveCreateDocument}
            disabled={createSaving}
            uploadPdf={
              createUserId.trim()
                ? (file) =>
                    uploadCandidaturePdf(
                      createUserId.trim(),
                      createFolderKeyRef.current,
                      file
                    )
                : undefined
            }
          />
          <FormControl size="small">
            <InputLabel id="create-cand-status-label">Statut</InputLabel>
            <Select
              labelId="create-cand-status-label"
              label="Statut"
              value={createCandStatus}
              onChange={(e) =>
                setCreateCandStatus(e.target.value as 'approved' | 'rejected' | 'pending')
              }
              disabled={createSaving}
            >
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="approved">Approuvée</MenuItem>
              <MenuItem value="rejected">Rejetée</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateCandidature} color="inherit" disabled={createSaving}>
            Annuler
          </Button>
          <Button
            onClick={() => void handleSaveCreateCandidature()}
            variant="contained"
            disabled={createSaving}
          >
            {createSaving ? 'Création…' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editCandidature)} onClose={closeEditCandidature} maxWidth="md" fullWidth>
        <DialogTitle>Modifier la candidature</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {editError && (
            <Alert severity="error" onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}
          <EntityAutocomplete
            label="Utilisateur"
            required
            options={editDialogUserOptions}
            valueId={editUserId}
            onChangeId={setEditUserId}
            disabled={editSaving}
            loading={!users}
            helperText="Rechercher par nom ou e-mail"
            orphanLabel={editUserId.trim() ? 'Utilisateur (hors liste chargée)' : undefined}
          />
          <EntityAutocomplete
            label="Poste"
            required
            options={editDialogPostOptions}
            valueId={editPostId}
            onChangeId={setEditPostId}
            disabled={editSaving}
            loading={!posts}
            helperText="Rechercher par titre ou nom d’école"
            orphanLabel={editPostId.trim() ? 'Poste (hors liste chargée)' : undefined}
          />
          <TextField
            label="Date"
            type="date"
            value={editCandDate}
            onChange={(e) => setEditCandDate(e.target.value)}
            disabled={editSaving}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            disabled={editSaving}
            multiline
            minRows={3}
          />
          <DocumentsListEditor
            documents={editDocuments}
            onAddDocumentUrl={(url) => setEditDocuments((d) => [...d, url])}
            onRemoveDocument={handleRemoveEditDocument}
            disabled={editSaving}
            uploadPdf={
              editCandidature && (editUserId.trim() || editCandidature.userId)
                ? (file) =>
                    uploadCandidaturePdf(
                      (editUserId.trim() || editCandidature.userId) as string,
                      editCandidature.id,
                      file
                    )
                : undefined
            }
          />
          <FormControl size="small">
            <InputLabel id="edit-cand-status-label">Statut</InputLabel>
            <Select
              labelId="edit-cand-status-label"
              label="Statut"
              value={editCandStatus}
              onChange={(e) =>
                setEditCandStatus(e.target.value as 'approved' | 'rejected' | 'pending')
              }
              disabled={editSaving}
            >
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="approved">Approuvée</MenuItem>
              <MenuItem value="rejected">Rejetée</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditCandidature} color="inherit" disabled={editSaving}>
            Annuler
          </Button>
          <Button
            onClick={() => void handleSaveCandidature()}
            variant="contained"
            disabled={editSaving}
          >
            {editSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={canEdit && confirmOpen}
        anchorEl={confirmAnchorEl}
        onClose={handleCancelAction}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" gutterBottom>
            Confirmer l&apos;action
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirmDescription}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button onClick={handleCancelAction} color="inherit" size="small">
              Annuler
            </Button>
            <Button
              onClick={handleConfirmAction}
              color="primary"
              variant="contained"
              size="small"
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Popover>
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer les candidatures sélectionnées</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedIds.length} candidature(s) seront définitivement supprimées. Les activités liées
            peuvent rester orphelines si elles existent encore.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button onClick={handleConfirmDeleteSelected} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Suppression…' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

